import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import {
  DatabaseTableSummary,
  DatabaseQueryResult,
  DatabaseInstance,
  CreatedDatabaseCredentials,
} from '@kcdash/shared';
import {
  Database,
  Plus,
  Play,
  AlertCircle,
  Clock,
  Table,
  Copy,
  Check,
  Trash2,
  RefreshCw,
  Eye,
  EyeOff,
  CheckCircle2,
  Code2,
  Server,
  Layers,
} from 'lucide-react';
import { Modal } from '../components/Modal.js';

export const DatabaseManagerPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  // Database Instances state
  const [databases, setDatabases] = useState<DatabaseInstance[]>([]);
  const [isLoadingDatabases, setIsLoadingDatabases] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [dbNameInput, setDbNameInput] = useState('roleplay');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdResult, setCreatedResult] = useState<CreatedDatabaseCredentials | null>(null);

  // Inspector & Queries state
  const [tables, setTables] = useState<DatabaseTableSummary[]>([]);
  const [query, setQuery] = useState('SELECT * FROM players LIMIT 20;');
  const [queryResult, setQueryResult] = useState<DatabaseQueryResult | null>(null);
  const [isLoadingTables, setIsLoadingTables] = useState(true);
  const [isExecuting, setIsExecuting] = useState(false);

  // UI helpers
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [visiblePasswords, setVisiblePasswords] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Active integration snippet tab
  const [activeSnippetTab, setActiveSnippetTab] = useState<'lua' | 'toml' | 'uri'>('lua');

  useEffect(() => {
    if (!id) return;
    loadDatabases();
    loadTables();
  }, [id]);

  const loadDatabases = async () => {
    if (!id) return;
    setIsLoadingDatabases(true);
    try {
      const data = await api.getDatabaseInstances(id);
      setDatabases(data);
    } catch (err: any) {
      console.error('Failed to load databases:', err);
    } finally {
      setIsLoadingDatabases(false);
    }
  };

  const loadTables = async () => {
    if (!id) return;
    setIsLoadingTables(true);
    try {
      const data = await api.getDatabaseTables(id);
      setTables(data);
    } catch (err: any) {
      console.error('Failed to load database tables:', err);
    } finally {
      setIsLoadingTables(false);
    }
  };

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2500);
  };

  const togglePasswordVisibility = (dbId: string) => {
    setVisiblePasswords((prev) => ({
      ...prev,
      [dbId]: !prev[dbId],
    }));
  };

  const handleCreateDatabase = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !dbNameInput.trim()) return;

    setIsSubmitting(true);
    setError(null);
    try {
      const result = await api.createDatabaseInstance(id, {
        name: dbNameInput.trim(),
        type: 'mysql',
      });
      setCreatedResult(result);
      setSuccessMsg(`MySQL databáze "${result.database}" byla úspěšně vytvořena!`);
      setTimeout(() => setSuccessMsg(null), 4000);
      setIsCreateModalOpen(false);
      setDbNameInput('economy');
      loadDatabases();
    } catch (err: any) {
      setError(err.message || 'Nepodařilo se vytvořit databázi.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteDatabase = async (dbId: string, name: string) => {
    if (!id) return;
    if (!window.confirm(`Opravdu chcete smazat databázi "${name}" a odebrat jejího uživatele?`)) {
      return;
    }

    try {
      await api.deleteDatabaseInstance(id, dbId);
      setSuccessMsg(`Databáze "${name}" byla smazána.`);
      setTimeout(() => setSuccessMsg(null), 3000);
      loadDatabases();
    } catch (err: any) {
      setError(err.message || 'Chyba při mazání databáze.');
    }
  };

  const handleResetPassword = async (dbId: string) => {
    if (!id) return;
    try {
      const res = await api.resetDatabasePassword(id, dbId);
      setSuccessMsg(`Nové heslo vygenerováno: ${res.newPassword}`);
      setTimeout(() => setSuccessMsg(null), 6000);
      loadDatabases();
    } catch (err: any) {
      setError(err.message || 'Chyba při obnově hesla.');
    }
  };

  const handleExecute = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !query.trim()) return;

    setError(null);
    setIsExecuting(true);
    try {
      const res = await api.executeDatabaseQuery(id, query.trim());
      setQueryResult(res);
    } catch (err: any) {
      setError(err.message || 'Chyba při vykonávání SQL dotazu');
      setQueryResult(null);
    } finally {
      setIsExecuting(false);
    }
  };

  const handleTableClick = (tableName: string) => {
    const newQuery = `SELECT * FROM ${tableName} LIMIT 25;`;
    setQuery(newQuery);
  };

  return (
    <div className="space-y-8">
      {/* Top Banner & Action */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-panel-border pb-6">
        <div>
          <div className="flex items-center gap-2">
            <Database className="w-6 h-6 text-brand-400" />
            <h2 className="text-xl font-bold text-neutral-100">MySQL & Databázový Manager</h2>
          </div>
          <p className="text-xs text-neutral-400 mt-1">
            Automatické vytváření izolovaných MySQL databází pro KCD2 Multiplayer, RP gamemody, inventáře a ukládání postav.
          </p>
        </div>

        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white shadow-lg shadow-brand-500/20 transition shrink-0"
        >
          <Plus className="w-4 h-4" />
          <span>Vytvořit databázi</span>
        </button>
      </div>

      {/* Notifications */}
      {successMsg && (
        <div className="p-4 rounded-xl bg-emerald-950/60 border border-emerald-800 text-emerald-300 text-xs flex items-center justify-between shadow-sm animate-fadeIn">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
            <span>{successMsg}</span>
          </div>
          <button onClick={() => setSuccessMsg(null)} className="text-emerald-400 hover:text-white">✕</button>
        </div>
      )}

      {error && (
        <div className="p-4 rounded-xl bg-red-950/60 border border-red-800 text-red-300 text-xs flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
            <span>{error}</span>
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-white">✕</button>
        </div>
      )}

      {/* Last Created Credentials Highlight Card */}
      {createdResult && (
        <div className="bg-panel-card border-2 border-brand-500/80 rounded-2xl p-6 shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 px-4 py-1 bg-brand-500 text-neutral-950 font-bold text-[10px] uppercase tracking-wider rounded-bl-xl">
            Právě vygenerováno
          </div>

          <div className="flex items-center gap-2 mb-4">
            <CheckCircle2 className="w-5 h-5 text-brand-400" />
            <h3 className="text-sm font-bold text-neutral-100">
              Přístupové údaje k MySQL databázi: {createdResult.database}
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <div className="p-3 bg-panel-subtle rounded-xl border border-panel-border/80">
              <span className="text-[10px] text-neutral-500 uppercase font-mono block">Host</span>
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs font-mono font-semibold text-neutral-200">{createdResult.host}</span>
                <button
                  onClick={() => handleCopy(createdResult.host, 'host')}
                  className="p-1 hover:text-brand-400 text-neutral-400 transition"
                  title="Kopírovat Host"
                >
                  {copiedKey === 'host' ? <Check className="w-3.5 h-3.5 text-brand-400" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>

            <div className="p-3 bg-panel-subtle rounded-xl border border-panel-border/80">
              <span className="text-[10px] text-neutral-500 uppercase font-mono block">Port</span>
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs font-mono font-semibold text-neutral-200">{createdResult.port}</span>
                <button
                  onClick={() => handleCopy(String(createdResult.port), 'port')}
                  className="p-1 hover:text-brand-400 text-neutral-400 transition"
                  title="Kopírovat Port"
                >
                  {copiedKey === 'port' ? <Check className="w-3.5 h-3.5 text-brand-400" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>

            <div className="p-3 bg-panel-subtle rounded-xl border border-panel-border/80">
              <span className="text-[10px] text-neutral-500 uppercase font-mono block">Databáze</span>
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs font-mono font-semibold text-neutral-200">{createdResult.database}</span>
                <button
                  onClick={() => handleCopy(createdResult.database, 'db')}
                  className="p-1 hover:text-brand-400 text-neutral-400 transition"
                  title="Kopírovat Název"
                >
                  {copiedKey === 'db' ? <Check className="w-3.5 h-3.5 text-brand-400" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>

            <div className="p-3 bg-panel-subtle rounded-xl border border-panel-border/80">
              <span className="text-[10px] text-neutral-500 uppercase font-mono block">Uživatel</span>
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs font-mono font-semibold text-neutral-200">{createdResult.username}</span>
                <button
                  onClick={() => handleCopy(createdResult.username, 'user')}
                  className="p-1 hover:text-brand-400 text-neutral-400 transition"
                  title="Kopírovat Uživatele"
                >
                  {copiedKey === 'user' ? <Check className="w-3.5 h-3.5 text-brand-400" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
          </div>

          <div className="p-3 bg-panel-subtle rounded-xl border border-panel-border/80 flex items-center justify-between mb-4">
            <div>
              <span className="text-[10px] text-neutral-500 uppercase font-mono block">Heslo k databázi</span>
              <span className="text-xs font-mono font-bold text-brand-400 select-all">{createdResult.password}</span>
            </div>
            <button
              onClick={() => handleCopy(createdResult.password, 'pass')}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-neutral-800 hover:bg-neutral-700 text-neutral-200 rounded-lg text-xs font-mono transition"
            >
              {copiedKey === 'pass' ? <Check className="w-3.5 h-3.5 text-brand-400" /> : <Copy className="w-3.5 h-3.5" />}
              <span>{copiedKey === 'pass' ? 'Zkopírováno' : 'Kopírovat heslo'}</span>
            </button>
          </div>

          {/* Integration Snippets Tabs */}
          <div className="bg-panel-input rounded-xl border border-panel-border p-4">
            <div className="flex items-center justify-between border-b border-panel-border/80 pb-2 mb-3">
              <div className="flex items-center gap-2">
                <Code2 className="w-4 h-4 text-brand-400" />
                <span className="text-xs font-semibold text-neutral-300">Předpřipravené konfigurace:</span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setActiveSnippetTab('lua')}
                  className={`px-2.5 py-1 rounded text-[11px] font-mono transition ${
                    activeSnippetTab === 'lua' ? 'bg-brand-600 text-white' : 'text-neutral-400 hover:text-neutral-200'
                  }`}
                >
                  Lua Script (oxmysql / KCD)
                </button>
                <button
                  onClick={() => setActiveSnippetTab('toml')}
                  className={`px-2.5 py-1 rounded text-[11px] font-mono transition ${
                    activeSnippetTab === 'toml' ? 'bg-brand-600 text-white' : 'text-neutral-400 hover:text-neutral-200'
                  }`}
                >
                  server.toml
                </button>
                <button
                  onClick={() => setActiveSnippetTab('uri')}
                  className={`px-2.5 py-1 rounded text-[11px] font-mono transition ${
                    activeSnippetTab === 'uri' ? 'bg-brand-600 text-white' : 'text-neutral-400 hover:text-neutral-200'
                  }`}
                >
                  Standardní URI
                </button>
              </div>
            </div>

            <div className="relative group">
              <pre className="text-[11px] font-mono text-neutral-300 p-3 bg-neutral-950/70 rounded-lg overflow-x-auto whitespace-pre">
                {activeSnippetTab === 'lua' && createdResult.luaConnectionString}
                {activeSnippetTab === 'toml' && createdResult.tomlSnippet}
                {activeSnippetTab === 'uri' && createdResult.connectionString}
              </pre>
              <button
                onClick={() => {
                  const content =
                    activeSnippetTab === 'lua'
                      ? createdResult.luaConnectionString
                      : activeSnippetTab === 'toml'
                      ? createdResult.tomlSnippet
                      : createdResult.connectionString;
                  handleCopy(content, 'snippet');
                }}
                className="absolute top-2 right-2 px-2.5 py-1 bg-neutral-800 hover:bg-neutral-700 text-neutral-300 rounded text-[10px] font-mono flex items-center gap-1 transition opacity-90 hover:opacity-100"
              >
                {copiedKey === 'snippet' ? <Check className="w-3 h-3 text-brand-400" /> : <Copy className="w-3 h-3" />}
                <span>{copiedKey === 'snippet' ? 'Zkopírováno' : 'Kopírovat kód'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Database Instances List */}
      <div className="bg-panel-card border border-panel-border rounded-2xl p-6 shadow-sm space-y-4">
        <div className="flex items-center justify-between pb-3 border-b border-panel-border">
          <div className="flex items-center gap-2">
            <Server className="w-4 h-4 text-brand-400" />
            <h3 className="text-sm font-bold text-neutral-200">Aktivní databáze pro tento server</h3>
          </div>
          <span className="text-xs text-neutral-500 font-mono">{databases.length} databází</span>
        </div>

        {isLoadingDatabases ? (
          <div className="py-8 text-center text-xs text-neutral-500 font-mono">Načítám databáze...</div>
        ) : databases.length === 0 ? (
          <div className="py-8 text-center space-y-2">
            <Database className="w-8 h-8 text-neutral-600 mx-auto" />
            <p className="text-xs text-neutral-400">Zatím nebyla vytvořena žádná dedikovaná MySQL databáze.</p>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="text-xs text-brand-400 hover:text-brand-300 font-medium underline"
            >
              Vytvořit první databázi jedním kliknutím
            </button>
          </div>
        ) : (
          <div className="divide-y divide-panel-border/60">
            {databases.map((db) => {
              const isPassVisible = visiblePasswords[db.id];
              return (
                <div key={db.id} className="py-4 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-neutral-200">{db.name}</span>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-mono bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                        {db.status}
                      </span>
                      <span className="text-[10px] text-neutral-500 font-mono uppercase bg-neutral-800 px-1.5 py-0.5 rounded">
                        {db.type}
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-mono text-neutral-400">
                      <span>DB: <strong className="text-neutral-300">{db.database}</strong></span>
                      <span>Uživatel: <strong className="text-neutral-300">{db.username}</strong></span>
                      <span>Host: <strong className="text-neutral-300">{db.host}:{db.port}</strong></span>
                    </div>

                    {db.password && (
                      <div className="flex items-center gap-2 pt-1">
                        <span className="text-[11px] text-neutral-500 font-mono">Heslo:</span>
                        <span className="text-xs font-mono text-brand-300 font-semibold select-all">
                          {isPassVisible ? db.password : '••••••••••••'}
                        </span>
                        <button
                          onClick={() => togglePasswordVisibility(db.id)}
                          className="text-neutral-500 hover:text-neutral-300 p-0.5"
                          title={isPassVisible ? 'Skrýt heslo' : 'Zobrazit heslo'}
                        >
                          {isPassVisible ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        </button>
                        <button
                          onClick={() => handleCopy(db.password!, `pass_${db.id}`)}
                          className="text-neutral-500 hover:text-brand-400 p-0.5"
                          title="Kopírovat heslo"
                        >
                          {copiedKey === `pass_${db.id}` ? (
                            <Check className="w-3.5 h-3.5 text-brand-400" />
                          ) : (
                            <Copy className="w-3.5 h-3.5" />
                          )}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => handleCopy(db.connectionString || '', `conn_${db.id}`)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-panel-subtle hover:bg-neutral-800 border border-panel-border text-neutral-300 text-xs font-mono transition"
                    >
                      {copiedKey === `conn_${db.id}` ? <Check className="w-3.5 h-3.5 text-brand-400" /> : <Copy className="w-3.5 h-3.5" />}
                      <span>{copiedKey === `conn_${db.id}` ? 'Zkopírováno' : 'Kopírovat URI'}</span>
                    </button>

                    <button
                      onClick={() => handleResetPassword(db.id)}
                      className="p-1.5 rounded-lg bg-panel-subtle hover:bg-neutral-800 border border-panel-border text-neutral-400 hover:text-neutral-200 transition"
                      title="Přegenerovat heslo"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                    </button>

                    <button
                      onClick={() => handleDeleteDatabase(db.id, db.database)}
                      className="p-1.5 rounded-lg bg-red-950/40 hover:bg-red-900/60 border border-red-900/60 text-red-400 hover:text-red-300 transition"
                      title="Smazat databázi"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* SQL Query Inspector & Schema Viewer */}
      <div className="space-y-4">
        <div>
          <div className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-brand-400" />
            <h3 className="text-base font-bold text-neutral-200">SQL Inspector & Náhled Tabulek</h3>
          </div>
          <p className="text-xs text-neutral-400 font-mono mt-0.5">
            Bezpečné prohlížení dat v tabulkách (pouze read-only operace SELECT).
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Tables Sidebar */}
          <div className="bg-panel-card border border-panel-border rounded-xl p-5 shadow-sm space-y-3">
            <div className="flex items-center gap-2 pb-3 border-b border-panel-border">
              <Table className="w-4 h-4 text-brand-400" />
              <h4 className="text-xs font-semibold text-neutral-200 uppercase tracking-wider font-mono">Tabulky</h4>
            </div>

            {isLoadingTables ? (
              <div className="text-xs text-neutral-500 font-mono py-4 text-center">Načítám tabulky...</div>
            ) : tables.length === 0 ? (
              <div className="text-xs text-neutral-500 font-mono py-4 text-center">Žádné tabulky nenalezeny</div>
            ) : (
              <div className="space-y-1">
                {tables.map((tbl) => (
                  <button
                    key={tbl.name}
                    onClick={() => handleTableClick(tbl.name)}
                    className="w-full text-left px-3 py-2 rounded-lg bg-panel-subtle hover:bg-neutral-800 border border-panel-border/60 transition flex items-center justify-between text-xs font-mono"
                  >
                    <span className="text-neutral-200 truncate">{tbl.name}</span>
                    <span className="text-neutral-500 text-[10px]">{tbl.rowCount} řádků</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Query Editor & Result Area */}
          <div className="lg:col-span-3 space-y-4">
            <form onSubmit={handleExecute} className="bg-panel-card border border-panel-border rounded-xl p-5 shadow-sm space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-neutral-300 font-mono uppercase">
                  Bezpečný SQL SELECT dotaz
                </label>
                <button
                  type="submit"
                  disabled={isExecuting}
                  className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-medium bg-brand-600 hover:bg-brand-500 text-white transition disabled:opacity-50"
                >
                  <Play className="w-3.5 h-3.5 fill-current" />
                  <span>{isExecuting ? 'Spouštím...' : 'Spustit dotaz'}</span>
                </button>
              </div>

              <textarea
                rows={3}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border font-mono text-xs text-neutral-200 focus:outline-none focus:border-brand-500"
              />

              <div className="text-[11px] text-neutral-500 font-mono flex items-center gap-1.5">
                <span>Zabezpečení: Destruktivní příkazy (DROP, DELETE, UPDATE) jsou v inspektoru blokovány.</span>
              </div>
            </form>

            {queryResult && (
              <div className="bg-panel-card border border-panel-border rounded-xl shadow-sm overflow-hidden">
                <div className="px-5 py-3 border-b border-panel-border bg-panel-subtle flex items-center justify-between text-xs font-mono text-neutral-400">
                  <span>Výsledek: {queryResult.totalRows} řádků</span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5 text-neutral-500" />
                    {queryResult.executionTimeMs} ms
                  </span>
                </div>

                <div className="overflow-x-auto max-h-96">
                  <table className="w-full text-left text-xs font-mono">
                    <thead className="bg-panel-subtle/80 border-b border-panel-border text-neutral-400 uppercase sticky top-0">
                      <tr>
                        {queryResult.columns.map((col) => (
                          <th key={col} className="px-4 py-2.5 whitespace-nowrap">
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-panel-border/50 text-neutral-300">
                      {queryResult.rows.map((row, idx) => (
                        <tr key={idx} className="hover:bg-panel-cardHover">
                          {queryResult.columns.map((col) => (
                            <td key={col} className="px-4 py-2 whitespace-nowrap">
                              {row[col] !== null && row[col] !== undefined ? (
                                String(row[col])
                              ) : (
                                <span className="text-neutral-600">null</span>
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Database Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Vytvořit novou herní MySQL databázi"
      >
        <form onSubmit={handleCreateDatabase} className="space-y-4">
          <p className="text-xs text-neutral-400">
            Zadejte označení databáze (např. <code className="text-brand-300">roleplay</code> nebo{' '}
            <code className="text-brand-300">economy</code>). Systém automaticky vytvoří MySQL schéma, vygeneruje unikátního
            uživatele a silné 24místné heslo a zobrazí vám kompletní konfiguraci pro Lua skripty a server.toml.
          </p>

          <div>
            <label className="block text-xs font-medium text-neutral-300 mb-1">Název / Účel databáze</label>
            <input
              type="text"
              required
              value={dbNameInput}
              onChange={(e) => setDbNameInput(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
              placeholder="např. roleplay, characters, items"
              className="w-full px-3 py-2 bg-panel-input border border-panel-border rounded-xl text-neutral-200 text-xs font-mono focus:outline-none focus:border-brand-500"
            />
            <span className="text-[11px] text-neutral-500 font-mono mt-1 block">
              Výsledné schéma: <code className="text-neutral-400">kcd_{dbNameInput || 'name'}_[auto]</code>
            </span>
          </div>

          <div className="flex justify-end gap-3 pt-3 border-t border-panel-border">
            <button
              type="button"
              onClick={() => setIsCreateModalOpen(false)}
              className="px-4 py-2 rounded-xl text-xs font-medium text-neutral-400 hover:text-neutral-200 transition"
            >
              Zrušit
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !dbNameInput.trim()}
              className="px-5 py-2 rounded-xl text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white transition disabled:opacity-50 shadow-md shadow-brand-500/20"
            >
              {isSubmitting ? 'Vytvářím a generuji...' : 'Vytvořit a vygenerovat údaje'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
